/*
 *   Copyright 2022-2024 SenX S.A.S.
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { h } from "@stencil/core";
import { DataModel } from "../../model/types";
import { Param } from "../../model/param";
import { Logger } from "../../utils/logger";
import { GTSLib } from "../../utils/gts.lib";
import { Utils } from "../../utils/utils";
import Leaflet from "leaflet";
import { MapLib } from "../../utils/map-lib";
import { ColorLib } from "../../utils/color-lib";
import { AntPath, antPath } from "leaflet-ant-path";
import domtoimage from "dom-to-image";
import "leaflet-edgebuffer";
import "leaflet.heat";
import { v4 } from "uuid";
import { isEqual } from "lodash";
export class DiscoveryMapComponent {
    constructor() {
        this.defOptions = Object.assign(Object.assign({}, new Param()), { map: {
                tiles: [],
                animate: false
            } });
        this.divider = 1000;
        this.pointslayer = [];
        this.popupAnchor = [0, -50];
        this.pathData = [];
        this.positionData = [];
        this.geoJson = [];
        this.pathDataLayer = Leaflet.featureGroup();
        this.positionDataLayer = Leaflet.featureGroup();
        this.tileLayerGroup = Leaflet.featureGroup();
        this.geoJsonLayer = Leaflet.featureGroup();
        this.poiLayer = new Leaflet.LayerGroup();
        this.heatmapLayer = Leaflet.featureGroup();
        this.shadowHeatmapLayer = Leaflet.featureGroup();
        this.firstDraw = true;
        this.initial = true;
        this.hidden = {};
        this.markerOver = false;
        this.tileLayers = [];
        this.pois = [];
        this.result = undefined;
        this.type = undefined;
        this.options = new Param();
        this.width = undefined;
        this.height = undefined;
        this.debug = false;
        this.parsing = false;
        this.toDisplay = [];
        this.innerOptions = undefined;
    }
    updateRes(newValue, oldValue) {
        if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
            this.result = GTSLib.getData(this.result);
            this.initial = true;
            setTimeout(() => this.drawMap(this.result || new DataModel(), true, true));
        }
    }
    optionsUpdate(newValue, oldValue) {
        var _a, _b;
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['optionsUpdate'], newValue, oldValue);
        let opts = newValue;
        if (!!newValue && typeof newValue === 'string') {
            opts = JSON.parse(newValue);
        }
        if (!isEqual(opts, this.innerOptions)) {
            this.innerOptions = Object.assign({}, opts);
            setTimeout(() => this.drawMap(this.result || new DataModel(), true, true));
            (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['optionsUpdate 2'], { options: this.innerOptions, newValue, oldValue });
        }
    }
    async resize() {
        const dims = Utils.getContentBounds(this.el.parentElement);
        this.width = dims.w;
        this.height = dims.h;
        if (this.map) {
            this.map.invalidateSize();
        }
        return Promise.resolve();
    }
    // noinspection JSUnusedLocalSymbols
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async export(type = 'png') {
        return await domtoimage.toPng(this.mapElement, { height: this.height, width: this.width });
    }
    async show(regexp) {
        var _a;
        GTSLib.flatDeep(((_a = this.result) !== null && _a !== void 0 ? _a : new DataModel()).data).forEach(gts => {
            var _a, _b, _c;
            const gtsName = (_c = (_b = (((_a = this.result) !== null && _a !== void 0 ? _a : new DataModel()).params || [])[gts.id]) === null || _b === void 0 ? void 0 : _b.key) !== null && _c !== void 0 ? _c : GTSLib.serializeGtsMetadata(gts);
            if (new RegExp(regexp).test(gtsName)) {
                this.hidden[gts.id] = false;
            }
        });
        this.drawMap(this.result || new DataModel(), true);
        return Promise.resolve();
    }
    async hide(regexp) {
        var _a;
        GTSLib.flatDeep(((_a = this.result) !== null && _a !== void 0 ? _a : new DataModel()).data).forEach(gts => {
            var _a, _b, _c;
            const gtsName = (_c = (_b = (((_a = this.result) !== null && _a !== void 0 ? _a : new DataModel()).params || [])[gts.id]) === null || _b === void 0 ? void 0 : _b.key) !== null && _c !== void 0 ? _c : GTSLib.serializeGtsMetadata(gts);
            if (new RegExp(regexp).test(gtsName)) {
                this.hidden[gts.id] = true;
            }
        });
        this.drawMap(this.result || new DataModel(), true);
        return Promise.resolve();
    }
    async hideById(id) {
        Object.keys(this.hidden).forEach(k => {
            if (new RegExp(id.toString()).test(k)) {
                this.hidden[k] = true;
            }
        });
        this.drawMap(this.result || new DataModel(), true);
        return Promise.resolve();
    }
    async showById(id) {
        Object.keys(this.hidden).forEach(k => {
            if (new RegExp(id.toString()).test(k)) {
                this.hidden[k] = false;
            }
        });
        this.drawMap(this.result || new DataModel(), true);
        return Promise.resolve();
    }
    // noinspection JSUnusedGlobalSymbols
    componentWillLoad() {
        var _a;
        this.parsing = true;
        this.LOG = new Logger(DiscoveryMapComponent, this.debug);
        if (typeof this.options === 'string') {
            this.innerOptions = JSON.parse(this.options);
        }
        else {
            this.innerOptions = this.options;
        }
        this.result = GTSLib.getData(this.result);
        this.divider = GTSLib.getDivider(this.innerOptions.timeUnit || 'us');
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['componentWillLoad'], {
            type: this.type,
            options: this.innerOptions,
            toDisplay: this.toDisplay,
        });
        const dims = Utils.getContentBounds(this.el.parentElement);
        this.width = dims.w;
        this.height = dims.h;
        this.parsing = false;
    }
    componentDidLoad() {
        this.height = Utils.getContentBounds(this.el.parentElement).h;
        this.drawMap(this.result || new DataModel());
    }
    drawMap(data, isRefresh = false, optionUpdate) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
        let tilesPromise;
        let zoomPromise;
        this.tileLayers = [];
        // noinspection JSUnusedAssignment
        let options = Utils.mergeDeep(this.defOptions, this.innerOptions || {});
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['drawMap', 'this.options 2 '], Object.assign({}, data.globalParams));
        options = Utils.mergeDeep(options, data.globalParams || {});
        optionUpdate = JSON.stringify(options) !== JSON.stringify(this.innerOptions);
        this.innerOptions = Object.assign({}, options);
        if (!!this.map) {
            this.map.invalidateSize(true);
        }
        (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['drawMap', 'data'], data);
        (_c = this.LOG) === null || _c === void 0 ? void 0 : _c.debug(['drawMap', 'this.height'], this.height);
        (_d = this.LOG) === null || _d === void 0 ? void 0 : _d.debug(['drawMap', 'this.options'], Object.assign({}, this.innerOptions));
        const dataList = GTSLib.flatDeep(GTSLib.flattenGtsIdArray(data.data, 0).res);
        data.params = data.params || [];
        const params = data.params;
        this.mapOpts = this.innerOptions.map || {};
        this.pointslayer = [];
        dataList.forEach(g => {
            if (GTSLib.isGts(g)) {
                if (!this.hidden[g.id]) {
                    this.hidden[g.id] = false;
                }
            }
        });
        this.pathData = MapLib.toLeafletMapPaths({
            gts: dataList,
            params,
            globalParams: this.innerOptions
        }, this.hidden, this.innerOptions.scheme) || [];
        this.positionData = MapLib.toLeafletMapPositionArray({
            gts: dataList,
            params,
            globalParams: this.innerOptions
        }, this.hidden, this.innerOptions.scheme) || [];
        this.geoJson = MapLib.toGeoJSON({ gts: dataList, params });
        if (this.mapOpts.mapType !== 'NONE') {
            const map = MapLib.mapTypes[this.mapOpts.mapType || 'DEFAULT'] || MapLib.mapTypes.DEFAULT;
            const mapOpts = {
                maxNativeZoom: this.mapOpts.maxNativeZoom || 19,
                maxZoom: this.mapOpts.maxZoom || 40,
                edgeBufferTiles: 5
            };
            if (map.attribution) {
                mapOpts.attribution = map.attribution;
            }
            if (map.subdomains) {
                mapOpts.subdomains = map.subdomains;
            }
            (_e = this.LOG) === null || _e === void 0 ? void 0 : _e.debug(['displayMap'], { isRefresh, optionUpdate });
            if (!isRefresh || optionUpdate) {
                (_f = this.LOG) === null || _f === void 0 ? void 0 : _f.debug(['displayMap'], 'map', map);
                this.tileLayers.push(map.link);
                // eslint-disable-next-line no-underscore-dangle
                if (!!this.tilesLayer && this.tilesLayer._url !== map.link) {
                    this.tileLayerGroup.removeLayer(this.tilesLayer);
                    this.tilesLayer = undefined;
                }
                if (!this.tilesLayer) {
                    this.tilesLayer = Leaflet.tileLayer(map.link, mapOpts);
                    this.tilesLayer.addTo(this.tileLayerGroup);
                    tilesPromise = new Promise(resolve => setTimeout(() => this.tilesLayer.on('load', () => resolve())));
                }
            }
        }
        if (!!this.map) {
            (_g = this.LOG) === null || _g === void 0 ? void 0 : _g.debug(['displayMap'], 'map exists');
            this.pathDataLayer.clearLayers();
            this.positionDataLayer.clearLayers();
            this.geoJsonLayer.clearLayers();
            this.heatmapLayer.clearLayers();
            this.shadowHeatmapLayer.clearLayers();
            this.poiLayer.clearLayers();
        }
        else {
            this.mainLayer = new Leaflet.LayerGroup([this.tileLayerGroup, this.heatmapLayer, this.geoJsonLayer, this.pathDataLayer, this.positionDataLayer]);
            this.map = Leaflet.map(this.mapElement, {
                preferCanvas: true,
                layers: this.mainLayer,
                zoomAnimation: true,
                maxBoundsViscocity: 1,
                worldCopyJump: true,
                maxBounds: new Leaflet.LatLngBounds(new Leaflet.latLng(-89.98155760646617, -180), new Leaflet.LatLng(89.99346179538875, 180)),
                maxZoom: this.mapOpts.maxZoom || 19
            });
            this.geoJsonLayer.bringToBack();
            Leaflet.control.scale().addTo(this.map);
            this.map.on('load', () => { var _a; return (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['displayMap', 'load'], this.map.getCenter().lng, this.currentLong, this.map.getZoom()); });
            this.map.on('zoomend', () => {
                if (!this.firstDraw) {
                    this.currentZoom = this.map.getZoom();
                    this.geoBounds.emit(this.map.getBounds().toBBoxString());
                }
            });
            this.map.on('moveend', () => {
                if (!this.firstDraw) {
                    this.currentLat = this.map.getCenter().lat;
                    this.currentLong = this.map.getCenter().lng;
                }
            });
        }
        const pathDataSize = (this.pathData || []).length;
        for (let i = 0; i < pathDataSize; i++) {
            const path = this.pathData[i];
            if (!!path) {
                this.updateGtsPath(path, data.params[i]);
            }
        }
        (_h = this.LOG) === null || _h === void 0 ? void 0 : _h.debug(['displayMap'], 'pathData', this.pathData);
        const positionsSize = (this.positionData || []).length;
        for (let i = 0; i < positionsSize; i++) {
            const pData = this.positionData[i];
            if (!!pData) {
                this.updatePositionArray(this.positionData[i], data.params[i]);
            }
        }
        (_j = this.LOG) === null || _j === void 0 ? void 0 : _j.debug(['displayMap'], 'positionData', this.positionData);
        (this.mapOpts.tiles || []).forEach(t => {
            var _a;
            (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['displayMap'], t);
            const tile = {
                subdomains: 'abcd',
                maxNativeZoom: this.mapOpts.maxNativeZoom || 19,
                maxZoom: this.mapOpts.maxZoom || 19
            };
            if (typeof t === 'string') {
                tile.url = t;
            }
            else if (typeof t === 'object') {
                tile.url = t.url;
                tile.maxZoom = this.mapOpts.maxZoom || 19;
                tile.maxNativeZoom = t.maxNativeZoom || this.mapOpts.maxNativeZoom || 19;
            }
            this.tileLayers.push(tile.url);
            const l = Leaflet.tileLayer(tile.url, {
                subdomains: 'abcd',
                maxNativeZoom: tile.maxNativeZoom || 19,
                maxZoom: this.mapOpts.maxZoom || 19
            });
            // eslint-disable-next-line no-underscore-dangle
            if (!this.tileLayerGroup.getLayers().find((l) => l._url === t.url)) {
                this.tileLayerGroup.addLayer(l);
            }
        });
        if (!isRefresh || optionUpdate) {
            this.tileLayerGroup.getLayers().forEach((l) => {
                // eslint-disable-next-line no-underscore-dangle
                if (!this.tileLayers.includes(l._url)) {
                    this.tileLayerGroup.removeLayer(l);
                }
            });
        }
        const geoJsonSize = (this.geoJson || []).length;
        for (let i = 0; i < geoJsonSize; i++) {
            const m = this.geoJson[i];
            if (!!m) {
                const color = ColorLib.getColor(i, this.innerOptions.scheme);
                const opts = {
                    style: () => {
                        var _a, _b;
                        return ({
                            color: (data.params && data.params[i]) ? (_a = data.params[i].datasetColor) !== null && _a !== void 0 ? _a : color : color,
                            fillColor: (data.params && data.params[i])
                                ? ColorLib.transparentize((_b = data.params[i].fillColor) !== null && _b !== void 0 ? _b : color)
                                : ColorLib.transparentize(color),
                        });
                    }
                };
                if (m.geometry.type === 'Point') {
                    opts.pointToLayer = (geoJsonPoint, latlng) => Leaflet.marker(latlng, {
                        icon: this.icon(color, (data.params && data.params[i]) ? (data.params[i].map || { marker: 'circle' }).marker : 'circle', (data.params && data.params[i])),
                        riseOnHover: true,
                        opacity: 1,
                    });
                }
                let display = '';
                const geoShape = Leaflet.geoJSON(m, opts);
                if (m.properties) {
                    Object.keys(m.properties).forEach(k => display += `<b>${k}</b>: ${m.properties[k]}<br />`);
                    geoShape.bindPopup(display);
                }
                geoShape.addTo(this.geoJsonLayer);
            }
        }
        let hasHeatmap = false;
        // HeatMap
        const size = (dataList || []).length;
        for (let i = 0; i < size; i++) {
            let p = (params || [])[i];
            if (!p) {
                p = Object.assign({}, new Param());
            }
            if (!!((_k = p.map) === null || _k === void 0 ? void 0 : _k.heatmap) && dataList[i].v[0] && dataList[i].v[0].length >= 3) {
                const g = dataList[i];
                let max = Number.MIN_SAFE_INTEGER;
                let min = Number.MAX_SAFE_INTEGER;
                hasHeatmap = true;
                const hasHeatmapData = g.v.map(v => {
                    max = Math.max(max, v[v.length - 1]);
                    min = Math.min(min, v[v.length - 1]);
                    Leaflet.circleMarker([v[1], v[2]], { radius: 1 }).addTo(this.shadowHeatmapLayer);
                    return [v[1], v[2], v[v.length - 1]];
                });
                Leaflet.heatLayer(hasHeatmapData, {
                    radius: ((_l = p.map) === null || _l === void 0 ? void 0 : _l.heatRadius) || ((_m = this.innerOptions.map) === null || _m === void 0 ? void 0 : _m.heatRadius) || 25,
                    minOpacity: ((_o = p.map) === null || _o === void 0 ? void 0 : _o.heatOpacity) || ((_p = this.innerOptions.map) === null || _p === void 0 ? void 0 : _p.heatOpacity) || 0.05,
                    maxZoom: 0,
                    max,
                    blur: ((_q = p.map) === null || _q === void 0 ? void 0 : _q.heatBlur) || ((_r = this.innerOptions.map) === null || _r === void 0 ? void 0 : _r.heatBlur) || 15,
                }).addTo(this.heatmapLayer);
            }
        }
        if (this.pathData.length > 0 || this.positionData.length > 0 || this.geoJson.length > 0 || hasHeatmap) {
            // Fit map to curves
            const group = Leaflet.featureGroup([this.geoJsonLayer, this.positionDataLayer, this.pathDataLayer, this.shadowHeatmapLayer]);
            this.bounds = group.getBounds();
            setTimeout(() => {
                var _a, _b, _c;
                if (!!this.bounds && this.bounds.isValid()) {
                    if ((this.currentLat || this.mapOpts.startLat) && (this.currentLong || this.mapOpts.startLong)) {
                        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['displayMap', 'setView'], 'fitBounds', 'already have bounds');
                        if (this.mapOpts.track) {
                            this.map.setView({
                                lat: this.mapOpts.startLat || this.bounds.getCenter().lat || 0,
                                lng: this.mapOpts.startLong || this.bounds.getCenter().lng || 0
                            }, this.mapOpts.startZoom || this.map.getBoundsZoom(this.bounds) || 10, { animate: true });
                        }
                        else {
                            this.map.setView({
                                lat: this.currentLat || this.mapOpts.startLat || 0,
                                lng: this.currentLong || this.mapOpts.startLong || 0
                            }, this.currentZoom || this.mapOpts.startZoom || 10, { animate: false });
                        }
                    }
                    else if (this.map && this.bounds) {
                        (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['displayMap', 'setView'], 'fitBounds', 'this.bounds', this.bounds);
                        this.map.fitBounds(this.bounds, { padding: [1, 1], animate: false, duration: 0 });
                    }
                    this.currentLat = this.map.getCenter().lat;
                    this.currentLong = this.map.getCenter().lng;
                }
                else {
                    (_c = this.LOG) === null || _c === void 0 ? void 0 : _c.debug(['displayMap', 'setView'], 'invalid bounds', { lat: this.currentLat, lng: this.currentLong });
                    this.map.setView({
                        lat: this.currentLat || this.mapOpts.startLat || 0,
                        lng: this.currentLong || this.mapOpts.startLong || 0
                    }, this.currentZoom || this.mapOpts.startZoom || 10, {
                        animate: false,
                        duration: 0
                    });
                    zoomPromise = new Promise(resolve => this.map.once('moveend zoomend', () => resolve()));
                }
            }, 10);
        }
        else {
            (_s = this.LOG) === null || _s === void 0 ? void 0 : _s.debug(['displayMap', 'no data'], 'lost', this.currentZoom, this.mapOpts.startZoom);
            if (!this.mapOpts.track) {
                this.currentLat = this.mapOpts.startLat || 0;
                this.currentLong = this.mapOpts.startLong || 0;
                this.currentZoom = this.mapOpts.startZoom || 2;
            }
            this.map.setView([
                this.currentLat || this.mapOpts.startLat || 0,
                this.currentLong || this.mapOpts.startLong || 0
            ], this.currentZoom || this.mapOpts.startZoom || 2, {
                animate: false,
                duration: 0
            });
            zoomPromise = new Promise(resolve => setTimeout(() => this.map.once('moveend zoomend', () => resolve())));
        }
        this.firstDraw = false;
        this.poiLayer.addTo(this.map);
        //  this.patchMapTileGapBug();
        void Promise.all([zoomPromise, tilesPromise])
            .then(() => setTimeout(() => {
            if (this.initial) {
                this.draw.emit();
                this.initial = false;
            }
        }, 500));
    }
    icon(color, marker = '', param) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        const c = `${ColorLib.sanitizeColor(color).slice(1)}`;
        const m = marker !== '' ? marker : 'circle';
        let iconUrl;
        let iconSize = [20, 20];
        let iconAnchor = [10, 10];
        if (((_a = param === null || param === void 0 ? void 0 : param.map) === null || _a === void 0 ? void 0 : _a.iconSize) || ((_c = (_b = this.innerOptions) === null || _b === void 0 ? void 0 : _b.map) === null || _c === void 0 ? void 0 : _c.iconSize)) {
            const size = ((_d = param === null || param === void 0 ? void 0 : param.map) === null || _d === void 0 ? void 0 : _d.iconSize) || ((_f = (_e = this.innerOptions) === null || _e === void 0 ? void 0 : _e.map) === null || _f === void 0 ? void 0 : _f.iconSize) || [48, 48];
            iconSize = GTSLib.isArray(size) ? size : [size, size];
            iconAnchor = [iconSize[0] / 2, iconSize[0] / 2];
        }
        if (marker.startsWith('http') || marker.startsWith('data:image')) {
            iconUrl = marker;
        }
        else if (marker.startsWith('<svg')) {
            iconUrl = 'data:image/svg+xml;base64,' + window.btoa(marker);
        }
        else {
            iconAnchor = [10, 22];
            const margin = 2;
            if (((_g = param === null || param === void 0 ? void 0 : param.map) === null || _g === void 0 ? void 0 : _g.iconSize) || ((_j = (_h = this.innerOptions) === null || _h === void 0 ? void 0 : _h.map) === null || _j === void 0 ? void 0 : _j.iconSize)) {
                const size = ((_k = param === null || param === void 0 ? void 0 : param.map) === null || _k === void 0 ? void 0 : _k.iconSize) || ((_m = (_l = this.innerOptions) === null || _l === void 0 ? void 0 : _l.map) === null || _m === void 0 ? void 0 : _m.iconSize) || iconSize;
                iconSize = GTSLib.isArray(size) ? size : [size, size];
                iconAnchor = [iconSize[0] / 2, iconSize[0] - margin];
            }
            iconUrl = `https://www.mapmarker.io/api/v2/font-awesome/v5/pin?icon=fa-${m}-solid&size=${iconSize[0]}&color=fff&background=${c}`;
        }
        return Leaflet.icon({ iconUrl, iconAnchor, iconSize, popupAnchor: this.popupAnchor });
    }
    getGTSDots(gts, param) {
        var _a;
        const dots = [];
        let icon;
        let size;
        switch (gts.render) {
            case 'path': {
                icon = this.icon(gts.color, gts.marker, param);
                size = ((_a = gts.path) !== null && _a !== void 0 ? _a : []).length;
                for (let i = 0; i < size; i++) {
                    const g = gts.path[i];
                    if (i < size - 1 || !gts.marker) {
                        const marker = Leaflet.circleMarker(g, {
                            radius: gts.baseRadius || MapLib.BASE_RADIUS,
                            color: gts.color,
                            fillColor: gts.color,
                            fillOpacity: 1,
                            riseOnHover: true,
                        });
                        this.addPopup(gts, g.val, g.ts, marker);
                        dots.push(marker);
                    }
                    else {
                        const marker = Leaflet.marker(g, { icon, riseOnHover: true, opacity: 1 });
                        this.addPopup(gts, g.val, g.ts, marker);
                        dots.push(marker);
                    }
                }
                break;
            }
            case 'marker':
                icon = this.icon(gts.color, gts.marker, param);
                size = (gts.path || []).length;
                for (let i = 0; i < size; i++) {
                    const g = gts.path[i];
                    const marker = Leaflet.marker(g, { icon, riseOnHover: true, opacity: 1 });
                    this.addPopup(gts, g.val, g.ts, marker);
                    dots.push(marker);
                }
                break;
            case 'weightedDots':
                size = (gts.path || []).length;
                for (let i = 0; i < size; i++) {
                    const p = gts.path[i];
                    let v = parseInt(p.val, 10);
                    if (isNaN(v)) {
                        v = 0;
                    }
                    const radius = 50 * v / ((gts.maxValue || 1) - (gts.minValue || 0));
                    const marker = Leaflet.circleMarker(p, {
                        radius: radius === 0 ? 1 : radius,
                        color: gts.borderColor || 'transparent',
                        fillColor: gts.color, fillOpacity: 0.5,
                        riseOnHover: true,
                        weight: 1
                    });
                    this.addPopup(gts, p.val, p.ts, marker);
                    dots.push(marker);
                }
                break;
            case 'dots':
            default:
                size = (gts.path || []).length;
                for (let i = 0; i < size; i++) {
                    const g = gts.path[i];
                    const marker = Leaflet.circleMarker(g, {
                        radius: gts.baseRadius || MapLib.BASE_RADIUS,
                        color: gts.color,
                        riseOnHover: true,
                        fillColor: gts.color,
                        fillOpacity: 1
                    });
                    this.addPopup(gts, g.val, g.ts, marker);
                    dots.push(marker);
                }
                break;
        }
        return dots;
    }
    updateGtsPath(gts, param) {
        const path = MapLib.pathDataToLeaflet(gts.path);
        const group = Leaflet.featureGroup();
        if ((path || []).length > 1 && !!gts.line && (gts.render === 'dots' || gts.render === 'path')) {
            if (!!this.mapOpts.animate) {
                group.addLayer(new AntPath(path || [], {
                    delay: 800, dashArray: [10, 100],
                    weight: 5, color: ColorLib.transparentize(gts.color, 0.5),
                    pulseColor: gts.color,
                    paused: false, reverse: false, hardwareAccelerated: true, hardwareAcceleration: true
                }));
            }
            else {
                group.addLayer(Leaflet.polyline(path || [], { color: gts.color, opacity: 0.5 }));
            }
        }
        const dots = this.getGTSDots(gts, param);
        const size = (dots || []).length;
        for (let i = 0; i < size; i++) {
            group.addLayer(dots[i]);
        }
        this.pathDataLayer.addLayer(group);
    }
    addPopup(positionData, value, ts, marker) {
        var _a, _b;
        if (!!positionData) {
            let date = ts;
            if (ts && ((_a = this.innerOptions.timeMode) !== null && _a !== void 0 ? _a : 'date') === 'date') {
                date = ((_b = GTSLib.toISOString(ts !== null && ts !== void 0 ? ts : 0, this.divider, this.innerOptions.timeZone, this.innerOptions.timeFormat)) !== null && _b !== void 0 ? _b : '')
                    .replace('T', ' ').replace(/\+[0-9]{2}:[0-9]{2}$/gi, '');
            }
            let content = '';
            content = `<p>${date}</p><p><b>${positionData.key}</b>: ${value === 0 ? 0 : value || 'na'}</p>`;
            Object.keys(positionData.properties || [])
                .forEach(k => content += `<b>${k}</b>: ${decodeURIComponent(positionData.properties[k])}<br />`);
            if (!!positionData.tooltip[ts]) {
                content += positionData.tooltip[ts];
            }
            marker.on('mouseover', () => {
                marker.openPopup();
                this.markerOver = true;
                if (!!this.poputTimeout) {
                    clearTimeout(this.poputTimeout);
                }
                this.poputTimeout = setTimeout(() => {
                    if (marker.isPopupOpen() && !this.markerOver)
                        marker.closePopup();
                }, this.innerOptions.tooltipDelay || 3000);
                this.dataPointOver.emit({
                    date: ts,
                    name: positionData.key,
                    value,
                    meta: positionData.properties
                });
            });
            this.markersRef = Object.assign({}, this.markersRef || {});
            if (!this.markersRef[positionData.key]) {
                this.markersRef[positionData.key] = {};
            }
            this.markersRef[positionData.key][ts] = marker;
            marker.bindPopup(content, { autoClose: true });
        }
        marker.on('mouseout', () => this.markerOver = false);
        marker.on('click', () => {
            const date = this.innerOptions.timeMode === 'date'
                ? GTSLib.zonedTimeToUtc(ts, 1, this.innerOptions.timeZone) * this.divider
                : ts;
            this.dataPointSelected.emit({ date, name: positionData.key, value, meta: positionData.properties });
            if (this.innerOptions.poi) {
                if (this.pois.find(p => p.lat === marker.getLatLng().lat && p.lng === marker.getLatLng().lng && p.name === positionData.key)) {
                    this.pois = this.pois.filter(p => p.lat !== marker.getLatLng().lat && p.lng !== marker.getLatLng().lng && p.name !== positionData.key);
                }
                else {
                    this.pois.push({
                        date,
                        name: positionData.key,
                        value,
                        meta: positionData.properties,
                        uid: v4(),
                        lat: marker.getLatLng().lat,
                        lng: marker.getLatLng().lng,
                    });
                }
                this.poiLayer.clearLayers();
                this.poi.emit(this.pois);
                this.pois.forEach(p => {
                    const icon = this.icon(this.innerOptions.poiColor, undefined, this.innerOptions);
                    const m = Leaflet.marker([p.lat, p.lng], { icon, riseOnHover: true, opacity: 1 });
                    this.poiLayer.addLayer(m);
                });
            }
        });
    }
    async setFocus(regexp, ts) {
        Object.keys(this.markersRef || {})
            .filter(s => new RegExp(regexp).test(s))
            .forEach(k => {
            if (!!this.markersRef[k][ts]) {
                this.markersRef[k][ts].openPopup();
            }
        });
        return Promise.resolve();
    }
    async unFocus() {
        Object.keys(this.markersRef)
            .forEach(k => {
            (Object.keys(this.markersRef[k] || {}))
                .forEach(ts => {
                if (!!this.markersRef[k][ts]) {
                    if (this.markersRef[k][ts].isPopupOpen() && !this.markerOver)
                        this.markersRef[k][ts].closePopup();
                }
            });
        });
        return Promise.resolve();
    }
    updatePositionArray(positionData, param) {
        var _a, _b, _c, _d;
        const group = Leaflet.featureGroup();
        const path = MapLib.updatePositionArrayToLeaflet(positionData.positions);
        if ((positionData.positions || []).length > 1 && !!positionData.line) {
            if (!!this.mapOpts.animate) {
                group.addLayer(antPath(path || [], {
                    delay: 800, dashArray: [10, 100],
                    weight: 5, color: ColorLib.transparentize(positionData.color, 0.5),
                    pulseColor: positionData.color,
                    paused: false, reverse: false, hardwareAccelerated: true, hardwareAcceleration: true
                }));
            }
            else {
                group.addLayer(Leaflet.polyline(path || [], { color: positionData.color, opacity: 0.5 }));
            }
        }
        let icon;
        let result;
        let inStep;
        let size;
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['updatePositionArray'], positionData);
        switch (positionData.render) {
            case 'marker':
                icon = this.icon(positionData.color, positionData.marker, param);
                size = (positionData.positions || []).length;
                for (let i = 0; i < size; i++) {
                    const p = positionData.positions[i];
                    const marker = Leaflet.marker({ lat: p[0], lng: p[1] }, { icon, riseOnHover: true, opacity: 1 });
                    this.addPopup(positionData, p[2], undefined, marker);
                    group.addLayer(marker);
                }
                (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['updatePositionArray', 'build marker'], icon);
                break;
            case 'coloredWeightedDots':
                (_c = this.LOG) === null || _c === void 0 ? void 0 : _c.debug(['updatePositionArray', 'coloredWeightedDots'], positionData);
                result = [];
                inStep = [];
                for (let j = 0; j < positionData.numColorSteps; j++) {
                    result[j] = 0;
                    inStep[j] = 0;
                }
                size = (positionData.positions || []).length;
                for (let i = 0; i < size; i++) {
                    const p = positionData.positions[i];
                    const radius = (parseInt(p[2], 10) - (positionData.minValue || 0)) * 50 / (positionData.maxValue || 50);
                    (_d = this.LOG) === null || _d === void 0 ? void 0 : _d.debug(['updatePositionArray', 'coloredWeightedDots', 'radius'], positionData.baseRadius * p[4]);
                    const marker = Leaflet.circleMarker({ lat: p[0], lng: p[1] }, {
                        radius,
                        color: positionData.borderColor || positionData.color,
                        fillColor: ColorLib.rgb2hex(positionData.colorGradient[p[5]].r, positionData.colorGradient[p[5]].g, positionData.colorGradient[p[5]].b),
                        riseOnHover: true,
                        fillOpacity: 0.3,
                    });
                    this.addPopup(positionData, p[2], undefined, marker);
                    group.addLayer(marker);
                }
                break;
            case 'weightedDots':
                size = (positionData.positions || []).length;
                for (let i = 0; i < size; i++) {
                    const p = positionData.positions[i];
                    const radius = (parseInt(p[2], 10) - (positionData.minValue || 0)) * 50 / (positionData.maxValue || 50);
                    const marker = Leaflet.circleMarker({ lat: p[0], lng: p[1] }, {
                        radius,
                        color: positionData.borderColor || positionData.color,
                        fillColor: positionData.color,
                        weight: 2,
                        riseOnHover: true,
                        fillOpacity: 0.3,
                    });
                    this.addPopup(positionData, p[2], undefined, marker);
                    group.addLayer(marker);
                }
                break;
            case 'dots':
            default:
                size = (positionData.positions || []).length;
                for (let i = 0; i < size; i++) {
                    const p = positionData.positions[i];
                    const marker = Leaflet.circleMarker({ lat: p[0], lng: p[1] }, {
                        radius: positionData.baseRadius || MapLib.BASE_RADIUS,
                        color: positionData.borderColor || positionData.color,
                        fillColor: positionData.color,
                        weight: 2,
                        riseOnHover: true,
                        fillOpacity: 0.7,
                    });
                    this.addPopup(positionData, p[2] === 0 ? 0 : p[2] || 'na', undefined, marker);
                    group.addLayer(marker);
                }
                break;
        }
        this.positionDataLayer.addLayer(group);
    }
    render() {
        return h("div", { key: 'c61f6abcdad5d232073d727ca904533ba0b536a4', class: "map-container", style: { width: `${this.width}px`, height: `${this.height}px` } }, h("div", { key: 'a7fe5b1cfb74921c9b3b913b58e07aca5ce7c356', ref: (el) => this.mapElement = el }));
    }
    static get is() { return "discovery-map"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["discovery-map.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["discovery-map.css"]
        };
    }
    static get properties() {
        return {
            "result": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "DataModel | string",
                    "resolved": "DataModel | string",
                    "references": {
                        "DataModel": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DataModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "result",
                "reflect": false
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChartType",
                    "resolved": "\"line\" | \"area\" | \"scatter\" | \"step-area\" | \"spline-area\" | \"spline\" | \"step\" | \"step-after\" | \"step-before\" | \"annotation\" | \"bar\" | \"bar-polar\" | \"display\" | \"image\" | \"map\" | \"gauge\" | \"linear-gauge\" | \"circle\" | \"compass\" | \"pie\" | \"doughnut\" | \"rose\" | \"tabular\" | \"svg\" | \"input:text\" | \"input:list\" | \"input:secret\" | \"input:autocomplete\" | \"input:chips\" | \"input:chips-autocomplete\" | \"input:slider\" | \"input:date\" | \"input:date-range\" | \"input:multi\" | \"input:multi-cb\" | \"button\" | \"button:radio\" | \"hidden\" | \"calendar\" | \"heatmap\" | \"profile\" | \"boxplot\" | \"dashboard\" | \"dashboard:flex\" | \"dashboard:scada\"",
                    "references": {
                        "ChartType": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::ChartType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "type",
                "reflect": false
            },
            "options": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "Param | string",
                    "resolved": "Param | string",
                    "references": {
                        "Param": {
                            "location": "import",
                            "path": "../../model/param",
                            "id": "src/model/param.ts::Param"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "options",
                "reflect": false,
                "defaultValue": "new Param()"
            },
            "width": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "width",
                "reflect": false
            },
            "height": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "height",
                "reflect": false
            },
            "debug": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "debug",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "width": {},
            "height": {},
            "parsing": {},
            "toDisplay": {},
            "innerOptions": {}
        };
    }
    static get events() {
        return [{
                "method": "draw",
                "name": "draw",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "dataPointOver",
                "name": "dataPointOver",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "dataPointSelected",
                "name": "dataPointSelected",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "geoBounds",
                "name": "geoBounds",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }, {
                "method": "poi",
                "name": "poi",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "resize": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "export": {
                "complexType": {
                    "signature": "(type?: 'png' | 'svg') => Promise<string>",
                    "parameters": [{
                            "name": "type",
                            "type": "\"svg\" | \"png\"",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<string>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "show": {
                "complexType": {
                    "signature": "(regexp: string) => Promise<void>",
                    "parameters": [{
                            "name": "regexp",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "DataModel": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DataModel"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "hide": {
                "complexType": {
                    "signature": "(regexp: string) => Promise<void>",
                    "parameters": [{
                            "name": "regexp",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "DataModel": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DataModel"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "hideById": {
                "complexType": {
                    "signature": "(id: number) => Promise<void>",
                    "parameters": [{
                            "name": "id",
                            "type": "number",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "DataModel": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DataModel"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "showById": {
                "complexType": {
                    "signature": "(id: number | string) => Promise<void>",
                    "parameters": [{
                            "name": "id",
                            "type": "string | number",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "DataModel": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DataModel"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "setFocus": {
                "complexType": {
                    "signature": "(regexp: string, ts: number) => Promise<void>",
                    "parameters": [{
                            "name": "regexp",
                            "type": "string",
                            "docs": ""
                        }, {
                            "name": "ts",
                            "type": "number",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "unFocus": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "result",
                "methodName": "updateRes"
            }, {
                "propName": "options",
                "methodName": "optionsUpdate"
            }];
    }
}
//# sourceMappingURL=discovery-map.js.map
